import { action, computed, makeObservable, observable, override } from 'mobx'
import type React from 'react'
import { createRef } from 'react'

import type AppStore from '@src/app/AppStore'
import type IUiStore from '@src/app/IUiStore'
import type {
  IdentityRow,
  MemberRow,
} from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import {
  AddNumberRow,
  ContactRow,
  getTypeLabel,
  InboxRow,
} from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import type { ActiveCall } from '@src/service/voice'

import VoicePhoneNumberSelectorController from './VoicePhoneNumberSelectorController'

export default class AddParticipantController
  extends VoicePhoneNumberSelectorController
  implements IUiStore
{
  override readonly mode: 'single' | 'multiple'
  override readonly lang = {
    SELECT_CUSTOM: 'Add',
    PLACEHOLDER: 'Members, inboxes, contacts, or numbers...',
  }

  readonly messageRef: React.MutableRefObject<HTMLInputElement | null> = createRef()

  message = ''
  messageFocused = false

  listeners: MemberRow[] = []

  constructor(
    app: AppStore,
    override call: ActiveCall,
  ) {
    super(app, call)
    this.mode = this.app.service.flags.flags.groupCallingAddMultipleParticipants
      ? 'multiple'
      : 'single'
    makeObservable<this, 'reset'>(this, {
      message: observable.ref,
      messageFocused: observable.ref,
      maxMessageLengthReached: computed,
      handleBack: action.bound,
      handleMessageChange: action.bound,
      handleMessageFocus: action.bound,
      handleMessageBlur: action.bound,
      reset: override,
    })
  }

  get maxMessageLengthReached() {
    return encodeURI(this.message).length >= 300
  }

  handleMessageChange(event: React.FormEvent<HTMLInputElement>) {
    this.message = event.currentTarget.value
  }

  handleMessageFocus() {
    this.messageFocused = true
  }

  handleMessageBlur() {
    this.messageFocused = false
  }

  handleAdd(phoneNumberIdForExternalParticipant?: string) {
    const userId = this.app.service.user.current?.id

    if (!userId) {
      return
    }

    const participants = this.selections.map((selection) =>
      this.call.createParticipantFromSelection(
        selection,
        {
          type: 'added',
          fromParticipantId: userId,
        },
        !!this.listeners.find((l) => l.identity === selection.to),
      ),
    )

    const promise = this.call.participants.add(
      participants,
      this.message,
      phoneNumberIdForExternalParticipant,
    )

    this.reset()

    return promise
  }

  handleBack() {
    this.reset()
  }

  override augmentIdentityRow(row: IdentityRow) {
    row.rowMeta.delete(row.id)

    if (row instanceof AddNumberRow || row instanceof ContactRow) {
      for (const phone of row.phones) {
        const numberAlreadyOnTheCall = this.call.participants.list.some(
          (participant) => participant.identifier === phone.number,
        )

        const disabled = numberAlreadyOnTheCall
        const tooltipDisabledReason = numberAlreadyOnTheCall
          ? 'This number is already on the call'
          : undefined
        const labelDisabledReason = numberAlreadyOnTheCall
          ? 'Already on the call'
          : undefined

        row.phoneNumberMeta.set(phone.number, {
          disabled,
          tooltipDisabledReason,
          labelDisabledReason,
        })
      }
    } else if (row instanceof InboxRow) {
      const allMembersAlreadyOnTheCall = row.phoneNumber.members.every(
        (member) =>
          !!this.call.participants.list.find(
            (participant) => participant.identity === member,
          ),
      )

      const disabled = allMembersAlreadyOnTheCall
      const tooltipDisabledReason = allMembersAlreadyOnTheCall
        ? 'All members of this inbox are already on the call'
        : undefined
      const labelDisabledReason = allMembersAlreadyOnTheCall
        ? 'Already on the call'
        : undefined

      row.rowMeta.set(row.id, {
        disabled,
        tooltipDisabledReason,
        labelDisabledReason,
      })
    }

    const disabled = row.phones.length === 0
    if (!disabled) {
      return
    }

    const tooltipDisabledReason = `${getTypeLabel(row.type)} has no numbers`
    const labelDisabledReason = 'No numbers'
    row.rowMeta.set(row.id, {
      disabled,
      tooltipDisabledReason,
      labelDisabledReason,
    })
  }

  protected override reset() {
    this.message = ''
    this.listeners = []
    super.reset()
  }

  override tearDown() {
    this.reset()
    this.filteredSubscription?.unsubscribe()
    super.tearDown()
  }
}
